/**
 * CollapsibleWebResults - Inline collapsible web search results for chat
 * 
 * Displays product results as a collapsible card within the chat flow.
 * Users can expand/collapse to view results without leaving the conversation.
 * 
 * Layout modes:
 * - Affiliate-only (default): 6 affiliate tiles (2 rows × 3 cols) from sponsoredProducts
 * - With in-house: In-house products in Row 1 + affiliate tiles below (when INHOUSE_SELLING_ENABLED)
 * 
 * Badge priority by source:
 * - inhouse: ⭐ Heseda 추천 (gold badge, highest priority) - has cart/wishlist buttons
 * - partner: 제휴 (purple badge)
 * - whitelist: 추천 (purple badge)
 * - web: 웹 (gray badge)
 */
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { logEvent } from '../../services/eventTrackingService';
import { auth } from '../../services/firebaseConfig';
import { useTranslation } from 'react-i18next';
import type { WebSearchResultItem, SearchTriggerSource } from '../../types/conversation';
import type { ProductSource } from '../../types/profile';
import type { CartItem, WishlistItem, GridProductCard } from '../../types/product';
import { INHOUSE_SELLING_ENABLED } from '../../config/adminConfig';
import { cleanProductTitle, isRelevantBeautyProduct } from '../../utils/productTitle';
import { openProductUrl } from '../../utils/openProductUrl';
import { COMPACT_BP } from '../../hooks/useResponsive';
import './CollapsibleWebResults.css';

const NARROW_BP = 480;

/** Detect Amazon/generic placeholder logos that aren't real product images */
function isPlaceholderImage(url: string): boolean {
  return /AmazonExports|amazon-adsystem|\/images\/G\/0[12]\//i.test(url);
}

export interface CollapsibleWebResultsProps {
  /** Array of web search results to display */
  results: WebSearchResultItem[];
  /** Row 3: Sponsored/Amazon products (affiliate) */
  sponsoredProducts?: GridProductCard[];
  /** Loading state for sponsored products */
  sponsoredLoading?: boolean;
  /** UI language: 'en' for English, 'ko' for Korean */
  language?: string;
  /** Initially expanded state */
  defaultExpanded?: boolean;
  /** Callback when a result is clicked */
  onClickResult?: (result: WebSearchResultItem, index: number) => void;
  /** Callback to add an in-house product to cart */
  onAddToCart?: (item: CartItem) => void;
  /** Callback to toggle wishlist for a product */
  onWishlistToggle?: (item: WishlistItem, source?: 'web-search') => void;
  /** Function to check if a product is in the wishlist */
  isInWishlist?: (productId: string) => boolean;
  /** Callback when "View Details" is clicked on an in-house product (opens ProductExperiencePanel) */
  onViewDetails?: (result: WebSearchResultItem) => void;
  /** Callback when sponsored product is clicked (for tracking) */
  onSponsoredClick?: (product: GridProductCard) => void;
  /** Callback to ask AI about a specific product — bridges Search → Chat */
  onAskAI?: (productName: string) => void;
  /** What triggered this search — used for contextual header banner */
  searchTriggerSource?: SearchTriggerSource;
}

/**
 * Badge system for product cards
 * 
 * Simplified for mobile - prioritize compliance and clarity:
 * 
 * SOURCE BADGE (top-left) - Where it ships from:
 * - 직접 배송: We own inventory, ship from our warehouse
 * - 제휴: Partner ships
 * - 외부 사이트: User goes to external site
 * 
 * SPONSORED BADGE (top-right) - Required by FTC/공정위:
 * - Only shows when isPaidPlacement=true
 * - Must be clearly labeled as advertising
 * 
 * Brand name is shown in displayLink/title, not in badge (saves space)
 */
function getSourceBadge(
  source: ProductSource | undefined,
  translate: (key: string) => string,
  displayLink?: string,
  isPaidPlacement?: boolean
): { text: string; className: string; isSponsored: boolean } {
  const isSponsored = isPaidPlacement === true;
  
  switch (source) {
    case 'inhouse':
      // In-house catalog - we ship directly
      return {
        text: translate('sourceBadge.shipsDirect'),
        className: 'badge--inhouse',
        isSponsored,
      };
    case 'partner': {
      // Show affiliate source name for consistency (e.g. "Amazon Ad" not generic "Partner")
      const isAmazon = displayLink?.toLowerCase().includes('amazon');
      return {
        text: isAmazon ? translate('sourceBadge.amazonAd') : translate('sourceBadge.partner'),
        className: isAmazon ? 'badge--amazon' : 'badge--partner',
        isSponsored,
      };
    }
    case 'whitelist':
      return {
        text: translate('sourceBadge.recommended'),
        className: 'badge--whitelist',
        isSponsored,
      };
    case 'web':
    default:
      // External web results
      return {
        text: translate('sourceBadge.external'),
        className: 'badge--web',
        isSponsored: false, // Web results are never sponsored
      };
  }
}

export default function CollapsibleWebResults({
  results,
  sponsoredProducts = [],
  sponsoredLoading = false,
  language = 'ko',
  defaultExpanded = true,
  onClickResult,
  onAddToCart,
  onWishlistToggle,
  isInWishlist,
  onViewDetails,
  onSponsoredClick,
  onAskAI,
  searchTriggerSource,
}: CollapsibleWebResultsProps): React.ReactElement | null {
  const { t } = useTranslation('chat');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  // Sponsored section starts collapsed on mobile to avoid dominating viewport
  const [isSponsoredExpanded, setIsSponsoredExpanded] = useState(window.innerWidth >= COMPACT_BP);
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set());
  const [isMobile, setIsMobile] = useState(window.innerWidth < COMPACT_BP);
  const [isNarrow, setIsNarrow] = useState(window.innerWidth < NARROW_BP);

  // Track viewport size for mobile-aware count + sync sponsored collapse
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < COMPACT_BP;
      setIsMobile(mobile);
      setIsNarrow(window.innerWidth < NARROW_BP);
      setIsSponsoredExpanded(!mobile);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // language prop kept for backward compatibility but translations now use i18n

  const handleImageError = useCallback((index: number) => () => {
    setFailedImages(prev => new Set(prev).add(index));
  }, []);

  const getHostname = (url: string): string => {
    try {
      return new URL(url).hostname;
    } catch {
      return url;
    }
  };

  // Deduplicate web results and filter out irrelevant items (e.g. movies in old cached results).
  // Uses id (stable) with link fallback so in-house products with link:'#' aren't collapsed.
  // Relevance check runs before dedup to avoid reserving keys for irrelevant entries.
  const deduplicatedResults = useMemo(() => {
    const seen = new Set<string>();
    return results.filter(r => {
      if (!isRelevantBeautyProduct(r.name || '', r.link, r.snippet || '')) return false;
      const key = r.id || r.link;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [results]);

  // Deduplicate sponsored products by ID and exclude any already in web results.
  // Cross-row exclusion compares URLs (not IDs) because sponsored product.id
  // never matches web result r.id — they come from different ID spaces.
  const deduplicatedSponsored = useMemo(() => {
    // Collect URLs already shown in web results (Rows 1-2) for cross-row exclusion
    const existingUrls = new Set(deduplicatedResults.map(r => r.link));
    
    // Dedupe within sponsored list by ID, exclude those whose URL is already in web results
    const seen = new Set<string>();
    return sponsoredProducts.filter(product => {
      const id = product.id || product.url;
      if (seen.has(id) || existingUrls.has(product.url)) {
        return false;
      }
      seen.add(id);
      return true;
    });
  }, [sponsoredProducts, deduplicatedResults]);

  // Count products by source for header
  const hasInHouse = deduplicatedResults.some(r => r.source === 'inhouse');
  const headerText = hasInHouse
    ? t('webSearch.recommendations')
    : t('webSearch.results');

  // Only hide if NO results AND NO sponsored products
  const hasResults = deduplicatedResults.length > 0;
  const hasSponsored = deduplicatedSponsored.length > 0 || sponsoredLoading;
  if (!hasResults && !hasSponsored) return null;

  return (
    <div className="collapsible-web-results">
      {/* Context banner — explains WHY these results appeared */}
      {searchTriggerSource && searchTriggerSource !== 'manual' && (
        <div className="collapsible-web-results__context-banner" data-testid="search-context-banner">
          <span className="collapsible-web-results__context-icon" aria-hidden="true">
            {searchTriggerSource === 'photo-analysis' ? '📸' : searchTriggerSource === 'login-auto' ? '🔄' : '✨'}
          </span>
          <span className="collapsible-web-results__context-text">
            {searchTriggerSource === 'photo-analysis'
              ? t('webSearch.triggerPhoto')
              : searchTriggerSource === 'voice-auto'
              ? t('webSearch.triggerVoice')
              : searchTriggerSource === 'login-auto'
              ? t('webSearch.triggerLogin')
              : t('webSearch.triggerAI')}
          </span>
        </div>
      )}

      {/* Collapsible Header - only show if there are actual web results */}
      {hasResults && (
        <button
          className="collapsible-web-results__header"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-controls="web-results-content"
        >
          <span className="collapsible-web-results__icon">
            {hasInHouse ? '🎯' : '🔍'}
          </span>
          <span className="collapsible-web-results__title">
            {headerText}
          </span>
          <span className="collapsible-web-results__count">
            ({isMobile 
              ? Math.min(deduplicatedResults.length, 4) + Math.min(deduplicatedSponsored.length, 4)
              : Math.min(deduplicatedResults.length, 6) + Math.min(deduplicatedSponsored.length, 6)}{t('webSearch.countSuffix')})
          </span>
          <span className={`collapsible-web-results__chevron ${isExpanded ? 'expanded' : ''}`}>
            ▼
          </span>
        </button>
      )}

      {/* Collapsible Content */}
      <div
        id="web-results-content"
        className={`collapsible-web-results__content ${isExpanded ? 'expanded' : ''}`}
      >
        {/* Sponsored/Affiliate Products — shown ABOVE organic results for visibility.
             1 ad tile per product, badge includes "Ad/광고" for FTC/공정위 compliance. */}
        {(deduplicatedSponsored.length > 0 || sponsoredLoading) && (
          <div className="collapsible-web-results__sponsored-section">
            {/* Collapsible header for sponsored products */}
            <button
              className="collapsible-web-results__header collapsible-web-results__header--sponsored"
              onClick={() => hasResults ? setIsSponsoredExpanded(!isSponsoredExpanded) : setIsExpanded(!isExpanded)}
              aria-expanded={hasResults ? isSponsoredExpanded : isExpanded}
              aria-controls="sponsored-results-content"
            >
              <span className="collapsible-web-results__icon">🛒</span>
              <span className="collapsible-web-results__title">
                {t('webSearch.recommendations')}
              </span>
              <span className="collapsible-web-results__count">
                ({Math.min(deduplicatedSponsored.length, 6)}{t('webSearch.countSuffix')})
              </span>
              <span className={`collapsible-web-results__chevron ${(hasResults ? isSponsoredExpanded : isExpanded) ? 'expanded' : ''}`}>
                ▼
              </span>
            </button>
            {(hasResults ? isSponsoredExpanded : isExpanded) && (
            <div
              id="sponsored-results-content"
              className="collapsible-web-results__grid collapsible-web-results__grid--sponsored"
            >
              {sponsoredLoading ? (
                // Skeleton loading state — 6 tiles
                [0, 1, 2, 3, 4, 5].map((i) => (
                  <div key={`skeleton-sponsored-${i}`} className="collapsible-result-card collapsible-result-card--skeleton">
                    <div className="skeleton-image" />
                    <div className="skeleton-content">
                      <div className="skeleton-title" />
                    </div>
                  </div>
                ))
              ) : (
                deduplicatedSponsored.slice(0, 6).map((product) => (
                  <a
                    key={product.id}
                    href={product.url}
                    target="_blank"
                    rel="noopener noreferrer sponsored"
                    className="collapsible-result-card collapsible-result-card--sponsored"
                    onClick={() => onSponsoredClick?.(product)}
                  >
                    {/* Badge includes "Ad/광고" for FTC/공정위 compliance */}
                    <div className="collapsible-result-card__badge badge--amazon">
                      {product.affiliateSource === 'amazon' 
                        ? t('sponsoredBadge.amazon')
                        : product.affiliateSource === 'coupang' 
                        ? t('sponsoredBadge.coupang') 
                        : product.affiliateSource === 'iherb'
                        ? t('sponsoredBadge.iherb')
                        : product.affiliateSource === 'yesstyle'
                        ? t('sponsoredBadge.yesstyle')
                        : t('sponsoredBadge.default')}
                    </div>

                    {/* Product Image */}
                    <div className="collapsible-result-card__image">
                      {product.image ? (
                        <img
                          src={product.image}
                          alt={product.name || 'Product'}
                          loading="lazy"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/placeholder-product.svg';
                          }}
                        />
                      ) : (
                        <div className="collapsible-result-card__image-placeholder">
                          📦
                        </div>
                      )}
                    </div>

                    <div className="collapsible-result-card__content">
                      {product.brand && (
                        <span className="collapsible-result-card__brand">{product.brand}</span>
                      )}
                      <h4 className="collapsible-result-card__title">
                        {cleanProductTitle(product.name || '')}
                      </h4>
                      <div className="collapsible-result-card__meta collapsible-result-card__meta--amazon">
                        <span className="collapsible-result-card__shop-link">
                          {t('sponsoredBadge.shop')}
                        </span>
                      </div>
                    </div>
                  </a>
                ))
              )}
            </div>
            )}
          </div>
        )}

        {/* Web/In-house results: dynamic columns based on tile count.
             Desktop: max 3 cols. Mobile: max 2 cols. Fewer tiles → fewer cols. */}
        {hasResults && (() => {
          const tiles = deduplicatedResults;
          let maxCols = 3;
          if (isNarrow) {
            maxCols = 1;
          } else if (isMobile) {
            maxCols = 2;
          }
          // Pick balanced column count: prefer even grid, avoid e.g. 3+1
          const cols = tiles.length <= maxCols
            ? tiles.length
            : tiles.length % maxCols === 0 || tiles.length % maxCols > 1
              ? maxCols
              : 2;
          return (
        <div
          className="collapsible-web-results__grid"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}
        >
          {tiles.map((result, index) => {
            const badge = getSourceBadge(result.source, t, result.displayLink, result.isPaidPlacement);
            const isInHouse = result.source === 'inhouse';
            // Use product ID for in-house products (for cart/inventory), fallback to link for web products
            const productId = result.id || result.link;
            const inWishlist = isInWishlist?.(productId) ?? false;
            
            /**
             * Convert search result to CartItem for add-to-cart
             */
            const handleAddToCartClick = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              if (onAddToCart && result.price) {
                onAddToCart({
                  id: productId,
                  name: result.name || 'Product',
                  priceUSD: typeof result.price === 'number' ? result.price : parseFloat(String(result.price)) || 0,
                  image: result.image || '',
                  url: result.link,
                  quantity: 1,
                  source: result.source || 'web',
                });
                // Log add-to-cart event
                const uid = auth.currentUser?.uid;
                if (uid) {
                  logEvent(uid, 'add_to_cart', { productId, name: result.name, source: result.source });
                }
              }
            };

            const handleWishlistClick = (e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              // Convert search result to WishlistItem - include source for proper handling
              const wishlistItem: WishlistItem = {
                id: productId,
                name: result.name || 'Product',
                priceUSD: typeof result.price === 'number' ? result.price : parseFloat(String(result.price)) || 0,
                image: result.image || '',
                url: result.link,
                source: result.source, // Include source for in-store vs web differentiation
              };
              onWishlistToggle?.(wishlistItem, 'web-search');
              // Log wishlist event
              const uid = auth.currentUser?.uid;
              if (uid) {
                logEvent(uid, 'wishlist_toggle', { productId, name: result.name, source: result.source });
              }
            };

            // Use <div> for all cards to avoid nesting <button> inside <a> (invalid HTML).
            // Web product cards open their link via onClick instead.
            const cardProps = isInHouse
              ? {
                  className: 'collapsible-result-card collapsible-result-card--inhouse',
                }
              : {
                  className: 'collapsible-result-card',
                  role: 'link' as const,
                  tabIndex: 0,
                  style: { cursor: 'pointer' } as React.CSSProperties,
                  onClick: () => {
                    onClickResult?.(result, index);
                    // Log product click event
                    const uid = auth.currentUser?.uid;
                    if (uid) {
                      logEvent(uid, 'product_clicked', { productId, name: result.name, source: result.source });
                    }
                    openProductUrl(result.link);
                  },
                  onKeyDown: (e: React.KeyboardEvent) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      onClickResult?.(result, index);
                      // Log product click event
                      const uid = auth.currentUser?.uid;
                      if (uid) {
                        logEvent(uid, 'product_clicked', { productId, name: result.name, source: result.source });
                      }
                      openProductUrl(result.link);
                    }
                  },
                };

            return (
              <div
                key={`web-result-${index}`}
                {...cardProps}
              >
                {/* Product image with placeholder fallback */}
                <div className="collapsible-result-card__image">
                  {result.image && !failedImages.has(index) && !isPlaceholderImage(result.image) ? (
                    <img
                      src={result.image}
                      alt={result.name || 'Product image'}
                      loading="lazy"
                      onError={handleImageError(index)}
                    />
                  ) : (
                    <div className="collapsible-result-card__image-placeholder">
                      🧴
                    </div>
                  )}
                </div>

                {/* Source Badge */}
                <div className={`collapsible-result-card__badge ${badge.className}`}>
                  {badge.text}
                </div>

                {/* SPONSORED badge - Required disclosure for paid placements (FTC/공정위 compliance).
                     Skip when source badge already contains "Ad" (e.g. "Amazon Ad"). */}
                {badge.isSponsored && badge.className !== 'badge--amazon' && (
                  <div className="collapsible-result-card__sponsored">
                    {t('webSearch.sponsored')}
                  </div>
                )}

                {/* Recommendation reason - show directly without wrapper text */}
              {result.reason && result.reason.length <= 60 && (
                <div className="collapsible-result-card__reason">
                  <span className="collapsible-result-card__reason-icon">💡</span>
                  {result.reason}
                </div>
              )}

              <div className="collapsible-result-card__content">
                {isInHouse && result.displayLink && (
                  <span className="collapsible-result-card__brand">{result.displayLink}</span>
                )}
                <h4 className="collapsible-result-card__title">
                  {cleanProductTitle(result.name || result.link)}
                </h4>

                {/* Price if available */}
                {result.price && (
                  <div className="collapsible-result-card__price">
                    ${typeof result.price === 'number' ? result.price.toFixed(2) : result.price}
                  </div>
                )}

                {result.snippet && (
                  <p className="collapsible-result-card__snippet">
                    {result.snippet}
                  </p>
                )}

                {/* In-house products: show action buttons */}
                {isInHouse ? (
                  <div className="collapsible-result-card__actions">
                    {/* Add to Cart button - only when selling features enabled */}
                    {INHOUSE_SELLING_ENABLED && (
                    <button
                      className="collapsible-result-card__cart-btn"
                      onClick={handleAddToCartClick}
                      aria-label={t('webSearch.addToCart')}
                      title={t('webSearch.addToCart')}
                    >
                      🛒
                    </button>
                    )}
                    {/* Wishlist toggle button */}
                    {onWishlistToggle && (
                    <button
                      className={`collapsible-result-card__wishlist-btn ${inWishlist ? 'active' : ''}`}
                      onClick={handleWishlistClick}
                      aria-label={inWishlist ? t('webSearch.removeFromWishlist') : t('webSearch.addToWishlist')}
                      title={inWishlist ? t('webSearch.removeFromWishlist') : t('webSearch.addToWishlist')}
                    >
                      {inWishlist ? '❤️' : '♡'}
                    </button>
                    )}
                    {/* View Details - opens ProductExperiencePanel for in-house products */}
                    <button
                      className="collapsible-result-card__view-link"
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewDetails?.(result);
                      }}
                    >
                      {t('webSearch.viewDetails')} →
                    </button>
                    {/* Ask AI about this product */}
                    {onAskAI && result.name && (
                      <button
                        className="collapsible-result-card__ask-ai-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (result.name) onAskAI(result.name);
                        }}
                        title={t('webSearch.askAI')}
                        aria-label={t('webSearch.askAI')}
                        data-testid="ask-ai-btn"
                      >
                        💬 {t('webSearch.askAIShort')}
                      </button>
                    )}
                  </div>
                ) : (
                  /* Web products: show domain, wishlist, Ask AI button, and arrow for external link */
                  <div className="collapsible-result-card__meta">
                    <span className="collapsible-result-card__domain">
                      {result.displayLink || getHostname(result.link)}
                    </span>
                    {onWishlistToggle && (
                    <button
                      className={`collapsible-result-card__wishlist-btn ${inWishlist ? 'active' : ''}`}
                      onClick={handleWishlistClick}
                      aria-label={inWishlist ? t('webSearch.removeFromWishlist') : t('webSearch.addToWishlist')}
                      title={inWishlist ? t('webSearch.removeFromWishlist') : t('webSearch.addToWishlist')}
                    >
                      {inWishlist ? '❤️' : '♡'}
                    </button>
                    )}
                    {onAskAI && result.name && (
                      <button
                        className="collapsible-result-card__ask-ai-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (result.name) onAskAI(result.name);
                        }}
                        title={t('webSearch.askAI')}
                        aria-label={t('webSearch.askAI')}
                        data-testid="ask-ai-btn"
                      >
                        💬 {t('webSearch.askAIShort')}
                      </button>
                    )}
                    <svg
                      className="collapsible-result-card__arrow"
                      width="14"
                      height="14"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M6 3L11 8L6 13"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
          );
        })()}

        {/* Mobile: "Collapse results" button so users can scroll back to chat */}
        {isMobile && isExpanded && hasResults && (
          <button
            className="collapsible-web-results__collapse-btn"
            onClick={() => setIsExpanded(false)}
            type="button"
          >
            ▲ {t('webSearch.collapseResults', { defaultValue: 'Collapse results' })}
          </button>
        )}

      </div>
    </div>
  );
}